import {ErrorHandler, Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import * as Sentry from '@sentry/browser';

const CHUNK_LOAD_ERROR = new RegExp(/Loading chunk [\w-]+ failed/);
const WEBSOCKET_CONNECT = new RegExp(/Failed to execute 'send' on 'WebSocket': Still in CONNECTING state./);
const RESIZE_OBSERVER = new RegExp(/ResizeObserver loop limit exceeded/); // Can safely ignore: https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
const RTE_STYLESHEETS_UNDEFINED = new RegExp(/styleSheets is undefined/); // RTEs on localhost
const RTE_DOC_UNDEFINED = new RegExp(/doc is undefined/); // RTEs on localhost

// A canonical UUID (8-4-4-4-12 hex) as a whole path segment.
const UUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;
// A wholly-numeric path segment (a record id). Anchored to segment boundaries so digits embedded
// in a word (were we ever to have e.g. /v2/) are left alone.
const NUMERIC_SEGMENT = /\/\d+(?=\/|$)/g;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  handleError(error: any) {
    // Catch Chunk Load Errors
    if (CHUNK_LOAD_ERROR.test(error.message)) {
      if (environment.sentry.enabled) {
        Sentry.captureException(error);
      }
      window.location.reload();
      return;
    }

    // Ignore websocket send Errors
    if (WEBSOCKET_CONNECT.test(error.message)) { return; }
    if (RESIZE_OBSERVER.test(error.message)) { return; }
    if (RTE_STYLESHEETS_UNDEFINED.test(error.message)) { return; }
    if (RTE_DOC_UNDEFINED.test(error.message)) { return; }

    // Send error to sentry if not handled above
    if (environment.sentry.enabled) {
      this.report(error);
    }

    console.error('Handled Error', error);
  }

  // Capture the error so that DIFFERENT errors form DIFFERENT Sentry issues while identical errors
  // still merge. Angular hands us whatever was thrown — often a raw HttpErrorResponse (a plain object
  // with no stack/message Sentry can read), which is why the catch-all issue collapsed everything onto
  // one fingerprint. We unwrap Angular's wrappers, then give Sentry a real Error plus an explicit
  // fingerprint per error shape.
  private report(error: any): void {
    const original = error?.ngOriginalError ?? error?.rejection ?? error;

    if (original instanceof HttpErrorResponse) {
      const endpoint = this.normalizeUrl(original.url);
      const synthetic = new Error(`HTTP ${original.status} ${original.statusText} — ${endpoint}`);
      synthetic.name = 'HttpErrorResponse';
      Sentry.captureException(synthetic, {
        fingerprint: ['http', String(original.status), endpoint],
        level: original.status >= 500 ? 'error' : 'warning',
        contexts: { http: { status: original.status, url: original.url } },
        extra: { serverError: original.error },
      });
      return;
    }

    // Real Errors already carry a stack + message that groups well — pass them through untouched.
    if (original instanceof Error) {
      Sentry.captureException(original);
      return;
    }

    // Any other object/primitive: synthesise an Error so Sentry has something to read, and fingerprint
    // on its shape with digits masked so per-record numbers don't fragment the grouping.
    const obj = (original ?? {}) as Record<string, any>;
    const synthetic = new Error(obj['message'] ?? this.safeStringify(original));
    synthetic.name = obj['name'] ?? 'NonError';
    Sentry.captureException(synthetic, {
      fingerprint: ['non-error', synthetic.name, this.signature(synthetic.message)],
    });
  }

  // Reduce a request URL to a stable endpoint for fingerprinting: drop the query string and collapse
  // per-record ids (numeric segments and UUIDs) so /api/one-to-ones/12913 and .../10563 share an issue.
  private normalizeUrl(url: string | null): string {
    if (!url) { return 'unknown'; }
    return url
      .split('?')[0]
      .replace(UUID_SEGMENT, '/:uuid')
      .replace(NUMERIC_SEGMENT, '/:id');
  }

  // A stable signature for a free-text message: mask digits so numbers don't fragment grouping, and
  // cap the length so an outlier long message can't spawn its own issue.
  private signature(message: string): string {
    return (message ?? '').replace(/\d+/g, '#').slice(0, 80);
  }

  private safeStringify(value: unknown): string {
    try {
      return JSON.stringify(value) ?? Object.prototype.toString.call(value);
    } catch {
      return Object.prototype.toString.call(value);
    }
  }

}
